/**
 * TestVectorsPanel — replay the frozen test vectors through the live
 * validator.
 *
 * Each test vector mirrors a file under
 * `theory/manuscripts/contrib/InvariantLifecycleGovernance/testvectors/`.
 * The demo's validator processes each vector and compares the verdict
 * against the expected outcome from the README, surfacing the result
 * with a pass/fail indicator. A failure here would mean the demo's
 * validator has drifted from the rulebook — the test suite enforces the
 * same invariant in CI.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import testVectorsData from "../data/testVectors.json" with { type: "json" };
import {
  validateEstablishment,
  validatePreservationWitness,
  validateProofArtifact,
  type EstablishmentInput,
  type IssuerCapabilityInput,
  type PreservationWitnessInput,
  type ProofArtifactInput,
  type Verdict,
} from "../algorithms/validatePreservation.js";

const ACCENT = "#fbbf24";

const SCENARIO_ICAP: IssuerCapabilityInput = {
  issuer_id: "ISSUER-ALPHA",
  may_sign_artifact_types: [
    "EstablishReceipt",
    "PreservationWitness",
    "DriftReceipt",
    "MigrationArtifact",
  ],
  invariant_ids: ["INV-A-NOAMBIENT", "INV-R-BUDGET"],
  kinds: ["Algebraic", "ResourceBound"],
  change_classes: ["code", "config", "deps", "accounting"],
};

interface Expected {
  readonly outcome: "accept" | "refuse";
  readonly rule: string | null;
}

interface BaseVector {
  readonly id: string;
  readonly file: string;
  readonly label: string;
  readonly scenario:
    | "preservation"
    | "establishment"
    | "proof"
    | "issuer-capability";
  readonly expected: Expected;
}

interface PreservationVector extends BaseVector {
  readonly scenario: "preservation";
  readonly witness: PreservationWitnessInput;
}

interface EstablishmentVector extends BaseVector {
  readonly scenario: "establishment";
  readonly establishment: EstablishmentInput;
}

interface ProofVector extends BaseVector {
  readonly scenario: "proof";
  readonly proof: ProofArtifactInput;
}

interface IcapVector extends BaseVector {
  readonly scenario: "issuer-capability";
  readonly scope: {
    readonly may_sign_artifact_types: readonly string[];
    readonly invariant_ids: readonly string[];
    readonly kinds: readonly ("Algebraic" | "ResourceBound")[];
    readonly change_classes: readonly string[];
  };
}

type Vector =
  | PreservationVector
  | EstablishmentVector
  | ProofVector
  | IcapVector;

const VECTORS = testVectorsData.vectors as readonly Vector[];

function runVector(vector: Vector): Verdict {
  if (vector.scenario === "preservation") {
    return validatePreservationWitness(vector.witness, SCENARIO_ICAP);
  }
  if (vector.scenario === "establishment") {
    return validateEstablishment(vector.establishment, SCENARIO_ICAP);
  }
  if (vector.scenario === "proof") {
    return validateProofArtifact(vector.proof);
  }
  return {
    outcome: "accept",
    subject: `IssuerCapability:${SCENARIO_ICAP.issuer_id}`,
  };
}

function isPass(verdict: Verdict, expected: Expected): boolean {
  if (verdict.outcome !== expected.outcome) return false;
  if (verdict.outcome === "refuse") {
    return expected.rule === null || verdict.rule === expected.rule;
  }
  return true;
}

export function TestVectorsPanel(): React.JSX.Element {
  const rows = VECTORS.map((v) => {
    const verdict = runVector(v);
    return {
      vector: v,
      verdict,
      pass: isPass(verdict, v.expected),
    };
  });
  const passing = rows.filter((r) => r.pass).length;
  const total = rows.length;

  return (
    <PanelSurface.PanelShell
      title="Test Vectors"
      subtitle="Frozen vectors replayed through the live validator"
      accentColor={ACCENT}
    >
      <PanelSurface.Card>
        <p className="m-0 text-[14.5px] leading-[1.7] text-[var(--ilg-text)]">
          The frozen v1.0.0 vectors under <PanelSurface.InlineCode>testvectors/</PanelSurface.InlineCode> are the
          validator's interoperability contract. The demo runs each one through
          the same pure function that powers the Witness Validator panel; a
          drift here would mean the demo's validator no longer matches the
          rulebook. The test suite (
          <PanelSurface.InlineCode>tests/validatePreservation.test.ts</PanelSurface.InlineCode>) enforces the same
          invariant in CI.
        </p>
      </PanelSurface.Card>

      <PanelSurface.Card
        glowColor={
          passing === total ? "rgba(34,197,94,0.16)" : "rgba(239,68,68,0.16)"
        }
      >
        <div className="flex items-baseline gap-3 flex-wrap">
          <span
            className="font-mono text-[28px] font-extrabold"
            style={{
              color: passing === total ? panelTokens.accentGreen : panelTokens.accentRed,
              textShadow:
                passing === total
                  ? `0 0 16px ${panelTokens.accentGreen}55`
                  : `0 0 16px ${panelTokens.accentRed}55`,
            }}
          >
            {passing} / {total}
          </span>
          <span className="text-[var(--ilg-text)] text-[14px]">
            vectors replayed cleanly against the live validator
          </span>
        </div>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>Vectors</PanelSurface.SectionHeading>
        <div className="mt-3.5 flex flex-col gap-2.5">
          {rows.map(({ vector, verdict, pass }) => (
            <div
              key={vector.id}
              className="grid grid-cols-[auto_1fr_auto] gap-3 items-center px-3.5 py-3 bg-[rgba(6,8,16,0.5)] rounded-xl"
              style={{
                border: `1px solid ${pass ? panelTokens.border : `${panelTokens.accentRed}55`}`,
              }}
            >
              <span
                aria-label={pass ? "passing vector" : "drifted vector"}
                className="w-[30px] h-[30px] rounded-lg flex items-center justify-center font-mono font-extrabold"
                style={{
                  background: pass ? `${panelTokens.accentGreen}22` : `${panelTokens.accentRed}22`,
                  border: `1px solid ${pass ? panelTokens.accentGreen : panelTokens.accentRed}55`,
                  color: pass ? panelTokens.accentGreen : panelTokens.accentRed,
                }}
              >
                {pass ? "✓" : "✗"}
              </span>

              <div className="min-w-0">
                <div className="text-[13.5px] text-[var(--ilg-bright)] font-semibold leading-[1.4]">
                  {vector.label}
                </div>
                <div className="mt-1 text-[11px] text-[var(--ilg-muted)] font-mono">
                  {vector.id} · {vector.file} · scenario: {vector.scenario}
                </div>
              </div>

              <div className="flex flex-col items-end gap-1">
                <span className="font-mono text-[11px] text-[var(--ilg-muted)]">
                  expected: {vector.expected.outcome}
                  {vector.expected.rule ? ` · ${vector.expected.rule}` : ""}
                </span>
                <span
                  className="font-mono text-[11px]"
                  style={{
                    color:
                      verdict.outcome === "accept"
                        ? panelTokens.accentGreen
                        : panelTokens.accentRed,
                  }}
                >
                  actual: {verdict.outcome}
                  {verdict.outcome === "refuse" ? ` · ${verdict.rule}` : ""}
                </span>
              </div>
            </div>
          ))}
        </div>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>Schema-level coverage</PanelSurface.SectionHeading>
        <p className="mt-3 text-[13.5px] leading-[1.7] text-[var(--ilg-text)]">
          The v1.0.0 ship-set carries <strong>19 frozen vectors</strong> across
          the 15 v0 schema files (14 artifact schemas plus common definitions).
          This panel exercises the structurally interesting cross-section
          (preservation, R-type accounting drift, A-type budget dependence,
          scope-violating issuer, missing InitialBudgetReceipt). The remaining
          purely-structural vectors are schema-gate-only and add no behaviour
          beyond what
          <PanelSurface.InlineCode>additionalProperties: false</PanelSurface.InlineCode> already enforces.
        </p>
        <div className="mt-3 flex gap-1.5 flex-wrap">
          {PanelSurface.badge("15 v0 schema files", panelTokens.accentBlue)}
          {PanelSurface.badge("19 frozen vectors", panelTokens.accentGreen)}
          {PanelSurface.badge("6 rule families", panelTokens.accentAmber)}
          {PanelSurface.badge("29 stable codes", panelTokens.accentPurple)}
        </div>
      </PanelSurface.Card>
    </PanelSurface.PanelShell>
  );
}
